import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps } from 'types/hooks';
import { PromoAuditLogRow } from 'types/tpo/auditLog';

const errorMessage: string = 'Something went wrong!';

interface AuditLogOutput {
    loading: boolean;
    error: any;
    data: PromoAuditLogRow[] | null;
    count: number;
}

function useFetchPromoAuditLog({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
}: HookInputProps): AuditLogOutput {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<PromoAuditLogRow[] | null>(null);
    const [count, setCount] = useState(0);

    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
                setCount(0);
            }
            setLoading(true);

            try {
                const response = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return response;
            } catch (err) {
                onFetchError(err);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }

    useEffect(() => {
        if (apiSuccess && apiData !== undefined) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData?.data);
        }
    }, [apiData?.data, apiSuccess]);

    function onFetchComplete(payload: any) {
        try {
            const rows: PromoAuditLogRow[] = payload?.data ?? [];
            const total: number = payload?.total_count ?? 0;
            let merged: PromoAuditLogRow[];
            if (variables?.offset !== 0 && Array.isArray(data)) {
                merged = [...data, ...rows];
            } else {
                merged = rows;
            }
            setData(merged);
            setCount(total);
        } catch (err) {
            console.error(errorMessage, err);
            onFetchError(err);
        } finally {
            setLoading(false);
        }
    }

    function onFetchError(err: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(err);
    }

    return { loading, error, data, count };
}

export { useFetchPromoAuditLog };
